import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { Company } from '@ups/xplat/api/dto';
import { SearchCompanyService } from '@ups/xplat/api/services';
import { dynamicTypeaheadCompanyFilter } from '@ups/xplat/dynamic-render';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadCompanyComponent),
  multi: true,
};

@Component({
  selector: 'ups-dynamic-typeahead-company',
  templateUrl: 'dynamic-typeahead-company.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  // WI 35584: template state is marked at each async write (loading, search results, target
  // updates) in the shared dynamic-render bases.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicTypeaheadCompanyComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly searchService = inject(SearchCompanyService);

  constructor() {
    super();
    this.queryFilter = dynamicTypeaheadCompanyFilter;
  }

  ngOnInit() {
    super.ngOnInit();
    // Bind in ngOnInit (not the constructor) so config.options is available to read activeOnly.
    this.searchQuery = this.performSearch.bind(this);
  }

  performSearch(searchString: string | null): Observable<Company[]> {
    return this.searchService.search(
      searchString,
      this.config?.options?.activeOnly === true,
      this.currentCompanyNumber()
    );
  }

  /**
   * The currently-selected or saved CompanyNumber, passed to the search so an active-only filter
   * never hides the value an existing record already holds (the company may since have gone
   * inactive in VP). Prefers the live selection, then the prefill/saved value.
   */
  private currentCompanyNumber(): number | string | null {
    const selected = this.selectedItem as
      { CompanyNumber?: number | string } | null | undefined;
    if (selected?.CompanyNumber != null) {
      return selected.CompanyNumber;
    }
    const fallback = (this.valueObjectId ?? this.config?.value) as
      number | string | null | undefined;
    return fallback ?? null;
  }
}
